/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { JSFunction, type WebInAppEvent } from '@ahm/common-helpers';

/**
 * Xin token MỚI từ app Ahamove qua native bridge (JSFunction).
 *
 * Dùng khi phiên webinapp 401 và activate token hiện tại không cứu được: app là
 * nguồn token duy nhất của webinapp → nhờ app cấp token mới (app tự refresh phiên
 * của nó), rồi ta activate (login) token đó. Chỉ khi bước này CŨNG fail mới hiện
 * dialog "mở lại từ app".
 *
 * Bridge trả token qua `messageEvent.data` (README @ahamove/js-function): bắt qua
 * CẢ callback của JSFunction.call LẪN window 'message' (belt-and-suspenders), lấy
 * cái nào tới trước. Timeout → null (bridge vắng ở dev/browser, hoặc app không phản hồi).
 */

// Bridge event xin token mới. 'refreshToken' = yêu cầu app cấp token mới (khác
// 'getToken' = trả token hiện tại). Nếu app dùng 'getToken' để trả token mới thì
// đổi hằng số này.
const TOKEN_REQUEST_EVENT: WebInAppEvent = 'refreshToken';
const TOKEN_REQUEST_TIMEOUT_MS = 8000;

function safeParseObject(s: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(s);
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Rút token (JWT) từ payload message của native bridge.
 *
 * Shape THẬT (đã đo trên stg qua nút debug, cả callback lẫn window 'message'):
 *   `{ webinapp: true, token: "<JWT>" }` — typeof object.
 * iOS webkit đôi khi gửi dạng CHUỖI JSON → parse trước. Sanity-check JWT (3 phần)
 * để không nhận nhầm message khác vô tình có field `token`.
 */
function extractToken(data: unknown): string | null {
  const obj =
    typeof data === 'string'
      ? safeParseObject(data)
      : data && typeof data === 'object'
        ? (data as Record<string, unknown>)
        : null;
  const raw = obj?.token ?? obj?.access_token;
  if (typeof raw !== 'string') return null;
  const token = raw.trim();
  return token.split('.').length === 3 ? token : null; // JWT = header.payload.signature
}

export function requestNewTokenFromApp(): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);

  return new Promise<string | null>((resolve) => {
    let settled = false;
    // eslint-disable-next-line prefer-const
    let timer: ReturnType<typeof setTimeout>;

    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(token);
    };

    const onMessage = (ev: MessageEvent) => {
      const token = extractToken(ev?.data);
      if (token) finish(token);
    };

    window.addEventListener('message', onMessage);
    timer = setTimeout(() => finish(null), TOKEN_REQUEST_TIMEOUT_MS);

    try {
      JSFunction.call({ name: TOKEN_REQUEST_EVENT }, (ev: MessageEvent) => {
        const token = extractToken(ev?.data);
        if (token) finish(token);
      });
    } catch {
      finish(null);
    }
  });
}
